import {
    Lesson,
    Question,
    QuestionSlide,
    MultipleChoiceQuestion,
    FillBlankQuestion,
    ShortAnswerQuestion,
} from '@all-for-one/shared-types'

/**
 * Pure content-selection logic for the lesson PDF exports (draft/answer-key
 * and worksheet) -- given a Lesson and a selection (section keys / levels),
 * which slides/questions belong on a document, and what their display text
 * is. Deliberately zero React/react-pdf import: this is the one slice of
 * pdf-documents.tsx that's genuinely pure data transformation, split out so
 * it's testable the way the rest of this project tests logic (plain `.ts`
 * unit tests -- this repo has zero `.tsx` component-render tests anywhere).
 * See docs/BLUEPRINT-2026-09-30-pdf-documents-decomposition.md.
 */

// ── Draft / Answer Key ────────────────────────────────────────────────────────

export const SECTION_MAP: Record<string, { label: string; stages: string[] }> = {
    assessment: { label: 'Pre/Post Assessment', stages: ['Pre-Assessment', 'Post-Assessment'] },
    cfu: { label: 'Check for Understanding', stages: ['Check for Understanding'] },
    iw: { label: 'Independent Work', stages: ['Independent Work'] },
}

export function getSlidesForSection(questionSlides: QuestionSlide[], sectionKey: string): QuestionSlide[] {
    const stages = SECTION_MAP[sectionKey]?.stages ?? []
    const matching = (questionSlides || []).filter(qs => stages.includes(qs.learningStage))
    if (sectionKey === 'assessment' && matching.length > 1) return [matching[0]]
    return matching
}

export function getAnswerText(q: Question): string {
    if (q.type === 'multiple-choice') return (q as MultipleChoiceQuestion).options[(q as MultipleChoiceQuestion).answer] ?? ''
    if (q.type === 'fill-blank') return (q as FillBlankQuestion).answer.join(', ')
    return (q as ShortAnswerQuestion).answer
}

export function getQuestionText(q: Question): string {
    if (q.type === 'fill-blank') return (q as FillBlankQuestion).question.join(' _____ ')
    return (q as any).question
}

// ── Worksheet ─────────────────────────────────────────────────────────────────

export const LEVEL_LETTERS: Record<string, string> = { Emerging: 'E', Intermediate: 'I', Advanced: 'A' }
export const WS_SECTION_ORDER = ['Pre-Assessment', 'Check for Understanding', 'Independent Work', 'Post-Assessment']

export function getQuestionsForLevelSection(lesson: Lesson, level: string, section: string) {
    const result: { slide: number; questions: Question[] }[] = []
    for (const qs of (lesson.questionSlides || []) as QuestionSlide[]) {
        if (qs.learningStage !== section) continue
        const dg = qs.questionsByDifficulty?.find(d => d.difficulty === level)
        if (dg && dg.questions.length > 0) result.push({ slide: qs.slide, questions: dg.questions })
    }
    return result
}

export function collectWorksheetImageUrls(lesson: Lesson, levels: string[], sections: string[]): string[] {
    const urls: string[] = []
    for (const level of levels) {
        for (const section of sections) {
            for (const group of getQuestionsForLevelSection(lesson, level, section)) {
                for (const q of group.questions) {
                    if (q.imageUrl) urls.push(q.imageUrl)
                    if (q.type === 'multiple-choice') {
                        const mc = q as MultipleChoiceQuestion
                        if (mc.optionImageUrls) {
                            Object.values(mc.optionImageUrls).forEach(url => { if (url) urls.push(url) })
                        }
                    }
                }
            }
        }
    }
    return urls
}

export function getWsQuestionTypes(lesson: Lesson, level: string, sections: string[]) {
    const types = new Set<string>()
    for (const s of sections)
        for (const g of getQuestionsForLevelSection(lesson, level, s))
            for (const q of g.questions) types.add(q.type)
    return types
}
